import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import { api } from '../lib/api';
import logo from '../assets/logo.png';

// ── Global Order Notification System ─────────────────────────────────
let _audioCtx = null;
let _audioUnlocked = false;

async function unlockAudio() {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (_audioCtx.state === 'suspended') await _audioCtx.resume();
    _audioUnlocked = _audioCtx.state === 'running';
    return _audioUnlocked;
  } catch (e) { return false; }
}

async function playOrderSound() {
  try {
    const ok = await unlockAudio();
    if (!ok) return;
    const ctx = _audioCtx;
    const beep = (t, freq, dur) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine'; o.frequency.setValueAtTime(freq, t);
      g.gain.setValueAtTime(0.6, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      o.start(t); o.stop(t + dur);
    };
    beep(ctx.currentTime, 880, 0.20);
    beep(ctx.currentTime + 0.25, 1046, 0.20);
    beep(ctx.currentTime + 0.50, 1318, 0.25);
  } catch(e) {}
}

function showBrowserNotification(count) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const n = new Notification('🔔 New Order — QR Digi Menu', {
    body: `${count} new order${count > 1 ? 's' : ''} waiting for confirmation!`,
    icon: '/logo.png',
    tag: 'new-order',
    requireInteraction: true,
  });
  n.onclick = () => { window.location.href = '/admin/orders'; n.close(); };
}

// Passive unlock on any user interaction
if (typeof window !== 'undefined') {
  ['click','touchstart','keydown'].forEach(e =>
    document.addEventListener(e, () => unlockAudio(), { passive: true })
  );
}

const NAV = [
  { path: '/admin', label: 'Dashboard', icon: '📊' },
  { path: '/admin/menu', label: 'Menu Items', icon: '🍽️' },
  { path: '/admin/categories', label: 'Categories', icon: '📂' },
  { path: '/admin/qr', label: 'QR Codes', icon: '📱' },
  { path: '/admin/orders', label: 'Orders', icon: '🛒', proOnly: true },
  { path: '/admin/catering', label: 'Catering', icon: '🎪', proOnly: true },
  { path: '/admin/settings', label: 'Settings', icon: '⚙️' },
];

export default function AdminLayout({ title, children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [plan, setPlan] = useState('basic');
  const [planStatus, setPlanStatus] = useState('');
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [slug, setSlug] = useState('');
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [soundEnabled] = useState(
    () => localStorage.getItem('qrdm_sound_enabled') === 'true'
  );
  const knownOrderIdsRef = useRef(null);
  const restaurantIdRef = useRef(null);
  const soundIntervalRef = useRef(null);

  // ── Load restaurant ───────────────────────────────────────────────
   useEffect(() => {
    api.get('/restaurants').then(data => {
      if (data.length > 0) {
        setPlan(data[0].plan || 'basic');
        setSlug(data[0].slug || '');
        restaurantIdRef.current = data[0].restaurant_id;
      }
    }).catch(() => {});
    api.get('/auth/me').then(userData => {
      const status = userData.plan_status || '';
      setPlanStatus(status);
      if (status === 'trial' && userData.trial_ends_at) {
        const days = Math.max(0, Math.floor(
          (new Date(userData.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)
        ));
        setTrialDaysLeft(days);
      }
    }).catch(() => {});
  }, []);

  // ── Global order polling — runs on every admin page ───────────────
  const checkOrders = useCallback(async () => {
    const rid = restaurantIdRef.current;
    if (!rid || plan !== 'pro') return;
    try {
      const orders = await api.get(`/restaurants/${rid}/orders`);
      const pendingOrders = orders.filter(o => o.status === 'pending');

      if (knownOrderIdsRef.current === null) {
        // First load — just record IDs, no alert
        knownOrderIdsRef.current = new Set(orders.map(o => o.order_id));
        return;
      }

      // Find new orders
      const newOrders = orders.filter(o => !knownOrderIdsRef.current.has(o.order_id));
      if (newOrders.length > 0) {
        newOrders.forEach(o => knownOrderIdsRef.current.add(o.order_id));
        const newPending = newOrders.filter(o => o.status === 'pending');
        if (newPending.length > 0) {
          setNewOrderCount(prev => prev + newPending.length);
          // Play sound if enabled
          if (soundEnabled) {
            playOrderSound();
            // Repeat sound every 4 seconds while on non-orders page
            if (!location.pathname.includes('/admin/orders')) {
              clearInterval(soundIntervalRef.current);
              soundIntervalRef.current = setInterval(() => playOrderSound(), 4000);
              setTimeout(() => clearInterval(soundIntervalRef.current), 60000); // stop after 1 min
            }
          }
          // Browser notification
          showBrowserNotification(newPending.length);
          // Request permission if not yet granted
          if (Notification.permission === 'default') {
            Notification.requestPermission();
          }
        }
      }
    } catch (e) {}
  }, [plan, soundEnabled, location.pathname]);

  // Poll every 15 seconds on all admin pages
  useEffect(() => {
    const interval = setInterval(checkOrders, 15000);
    return () => { clearInterval(interval); clearInterval(soundIntervalRef.current); };
  }, [checkOrders]);

  // Stop sound repeat when navigating to orders page
  useEffect(() => {
    if (location.pathname.includes('/admin/orders')) {
      clearInterval(soundIntervalRef.current);
      setNewOrderCount(0);
    }
  }, [location.pathname]);

  const isPro = plan === 'pro' || planStatus === 'trial';
  const [showTrialModal, setShowTrialModal] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="admin-layout">
      <style>{`
        @keyframes orderPulse {
          0%, 100% { box-shadow: 0 2px 8px rgba(255,107,53,0.4); }
          50% { box-shadow: 0 4px 20px rgba(255,107,53,0.8); }
        }
      `}</style>
      <aside className="sidebar">

        {/* Logo */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 64,
        }}>
          <Link to="/admin" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <img
              src={logo}
              alt="QR Digi Menu"
              style={{
                height: 38,
                width: 'auto',
                maxWidth: 180,
                display: 'block',
                objectFit: 'contain',
                background: 'white',
                padding: '4px 10px',
                borderRadius: 6,
              }}
            />
          </Link>
        </div>

        {/* New Order Alert — shows on all admin pages */}
        {newOrderCount > 0 && !location.pathname.includes('/admin/orders') && (
          <Link to="/admin/orders" onClick={() => setNewOrderCount(0)} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'linear-gradient(135deg, #ff6b35, #f7c59f)',
            margin: '8px 12px', borderRadius: 10, padding: '10px 14px',
            textDecoration: 'none', animation: 'orderPulse 1.5s infinite',
          }}>
            <span style={{ fontSize: 20 }}>🔔</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'white' }}>
                {newOrderCount} New Order{newOrderCount > 1 ? 's' : ''}!
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>
                Tap to view & confirm
              </div>
            </div>
          </Link>
        )}

        {/* Plan Badge */}
        <div style={{ padding: '8px 16px 4px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: isPro ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(255,255,255,0.1)',
            color: isPro ? '#0f1117' : 'rgba(255,255,255,0.7)',
            padding: '4px 12px', borderRadius: 20,
            fontSize: 12, fontWeight: 700,
          }}>
            {planStatus === 'trial' ? `🎁 Pro Trial · ${trialDaysLeft}d left` : isPro ? '⭐ Pro Plan' : '🔒 Basic Plan'}
          </div>
        </div>

        <nav className="sidebar-nav">
          {NAV.map(n => {
            const isLocked = n.proOnly && !isPro;
            return (
              <Link
                key={n.path}
                to={isLocked ? '/admin/settings' : n.path}
                className={`sidebar-link ${location.pathname === n.path ? 'active' : ''}`}
                style={isLocked ? { opacity: 0.5 } : {}}
                title={isLocked ? 'Upgrade to Pro to access this feature' : ''}
              >
                <span style={{ fontSize: 16 }}>{n.icon}</span>
                {n.label}
                {isLocked && <span style={{ fontSize: 10, marginLeft: 'auto', background: 'rgba(255,255,255,0.2)', padding: '1px 6px', borderRadius: 10 }}>PRO</span>}
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          {user && (
            <Link
              to="/admin/profile"
              className="sidebar-user"
              style={{ textDecoration: 'none', cursor: 'pointer' }}
            >
              <div className="sidebar-avatar">
                {user.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="sidebar-user-info">
                <div className="sidebar-user-name">{user.name}</div>
                <div className="sidebar-user-email">{user.email}</div>
              </div>
            </Link>
          )}
          <button onClick={handleLogout} className="sidebar-logout">
            🚪 Sign Out
          </button>
        </div>
      </aside>

      <main className="admin-content">
        <div className="admin-header">
          <h1 className="admin-title">{title}</h1>
                    <div className="flex gap-2 items-center">
           {(planStatus === 'free' || planStatus === '' || !planStatus) && (
              <button
                onClick={() => setShowTrialModal(true)}
                style={{
                  background: 'linear-gradient(135deg, #4ade80, #16a34a)',
                  color: 'white', padding: '6px 14px', borderRadius: 20,
                  fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                🎁 Start 30-Day Free Trial
              </button>
            )}
            {planStatus === 'trial' && trialDaysLeft !== null && (
              <Link to="/admin/settings" style={{
                background: 'linear-gradient(135deg, #4ade80, #16a34a)',
                color: 'white', padding: '6px 14px', borderRadius: 20,
                fontSize: 13, fontWeight: 700, textDecoration: 'none',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                🎁 {trialDaysLeft} days left
              </Link>
            )}
            {(planStatus === 'expired' || planStatus === 'cancelled') && (
              <Link to="/admin/settings" style={{
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: '#0f1117', padding: '6px 14px', borderRadius: 20,
                fontSize: 13, fontWeight: 700, textDecoration: 'none',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                ⭐ Upgrade Now
              </Link>
            )}
            {plan === 'basic' && planStatus === 'active' && (
              <Link to="/admin/settings" style={{
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: '#0f1117', padding: '6px 14px', borderRadius: 20,
                fontSize: 13, fontWeight: 700, textDecoration: 'none',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                ⭐ Upgrade to Pro
              </Link>
            )}
            <a
              href={`${window.location.origin}/menu/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline btn-sm"
            >
              👁️ View Public Menu
            </a>
          </div>
        </div>
        <div className="admin-body">
          {children}
        </div>
      </main>
          {/* ── Trial Modal ── */}
      {showTrialModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: 24
        }}>
          <div style={{
            background: 'white', borderRadius: 20, padding: '40px 32px',
            maxWidth: 420, width: '100%', textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <img src={logo} alt="QR Digi Menu" style={{ height: 40, borderRadius: 8 }} />
              <span style={{ fontSize: 18, fontWeight: 800, color: '#1a5c2a' }}>QR Digi Menu</span>
            </div>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎁</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1a1a1a', marginBottom: 8 }}>
              Start Your 30-Day Free Trial
            </h2>
            <p style={{ fontSize: 14, color: '#666', marginBottom: 24, lineHeight: 1.6 }}>
              Get full Pro access — no credit card required.
            </p>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '16px 20px', marginBottom: 24, textAlign: 'left' }}>
              {['✅ Online ordering system', '✅ Real-time order notifications', '✅ Catering request management', '✅ Auto order confirmation emails', '✅ 15-language menu translation', '✅ Unlimited QR menu scans', '✅ No credit card required', '✅ 30 days completely free', '✅ Downgrade to free plan anytime'].map(f => (
                <div key={f} style={{ fontSize: 14, color: '#166534', padding: '4px 0' }}>{f}</div>
              ))}
            </div>
            <button
              onClick={async () => {
                try {
                  await api.post('/auth/start-trial', {});
                  window.location.reload();
                } catch (err) {
                  alert(err.message || 'Failed to start trial.');
                }
              }}
              style={{
                width: '100%', padding: '14px', background: 'linear-gradient(135deg, #4ade80, #16a34a)',
                color: 'white', border: 'none', borderRadius: 12,
                fontSize: 16, fontWeight: 700, cursor: 'pointer', marginBottom: 12
              }}
            >
              🎁 Start My Free Trial
            </button>
            <button
              onClick={() => setShowTrialModal(false)}
              style={{
                width: '100%', padding: '12px', background: 'white',
                color: '#666', border: '1.5px solid #e5e7eb', borderRadius: 12,
                fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}
    </div>
  );
}